import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { countBy, isEqual } from 'lodash'
import {
  Filter,
  PaginatedAndSortableTable,
  StatusBadge,
  TableHeader,
  ComponentFailure,
  Spinner,
  Tooltip,
  QuickFilterCard,
  SearchInput,
  TableHeading,
  NoTableResults,
  //DownloadTableButton,
} from 'components'
import {
  ParticipantsFilterDialog,
  NotificationStatusModal,
} from '../components'
import { TableColumn } from 'lp-components'
import {
  formatDate,
  dateSorter,
  displayAttribute,
  sanitizeForSearch,
} from 'utils'
import { selectors as apiSelectors } from 'lp-redux-api'
import { ServicesAgreementParticipantsTooltip } from 'config/automatic-rollovers'
import { ProductAgreementType } from 'config/products'
import { NotificationStatuses } from 'config/notification-services'
import { Icon as SuccessIcon } from 'images/status-icons/checkmark-green.svg'
import { Icon as ClockIcon } from 'images/status-icons/clock-new.svg'
import { Icon as AlertIcon } from 'images/status-icons/alert-circle.svg'
import { Icon as XcircleIcon } from 'images/status-icons/x-with-circle.svg'
import { Icon as EvelopeIcon } from 'images/status-icons/envelope.svg'
import { Icon as QuestionIcon } from 'images/questionmark-circle.svg'
import { Icon as InfoIcon } from 'images/info-search.svg'

const propTypes = {
  notificationServicesAgreementParticipants: PropTypes.arrayOf(
    Types.notificationParticipant
  ),
  fetchNotificationServicesAgreementParticipants: PropTypes.func.isRequired,
  isFetchingNotificationServicesAgreementParticipants:
    PropTypes.bool.isRequired,
  servicesAgreementId: PropTypes.string.isRequired,
  baseFilters: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  filters: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  handleFilter: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  handleSearch: PropTypes.func.isRequired,
  fieldMappings: PropTypes.arrayOf(PropTypes.object).isRequired,
}

const defaultProps = {
  notificationServicesAgreementParticipants: null,
}

const TABLE_ID = 'agreements-participants-table'

const ParticipantsHeader = ({ children }) => {
  return (
    <div className="services-agreement-participants-header">
      <h2>Participants</h2>
      {children}
    </div>
  )
}

function NotificationsAgreementParticipants({
  notificationServicesAgreementParticipants,
  fetchNotificationServicesAgreementParticipants,
  isFetchingNotificationServicesAgreementParticipants,
  servicesAgreementId,
  baseFilters,
  filters,
  handleFilter,
  search,
  handleSearch,
  //fieldMappings,
}) {
  const [errorExists, setErrorExists] = useState(false)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [showDeliveryStatusModal, setShowDeliveryStatusModal] = useState(false)

  const hasFiltersApplied = !isEqual(filters, baseFilters)

  useEffect(() => {
    async function getNotificationServicesAgreementParticipants() {
      try {
        await fetchNotificationServicesAgreementParticipants(
          servicesAgreementId
        )
      } catch {
        setErrorExists(true)
      }
    }
    // will need to add trigger-refetch logic
    if (!notificationServicesAgreementParticipants)
      getNotificationServicesAgreementParticipants()
  }, [notificationServicesAgreementParticipants])

  const noServicesAgreementParticipants =
    !notificationServicesAgreementParticipants?.length

  const toggleFilter = (filterLabel) => {
    handleFilter({
      status: filters.status.includes(filterLabel)
        ? filters.status.filter((status) => status !== filterLabel)
        : filters.status.concat(filterLabel),
    })
  }

  const counts = countBy(
    notificationServicesAgreementParticipants,
    'vendorDeliveryStatus'
  )

  const filteredParticipants = useMemo(() => {
    if (!notificationServicesAgreementParticipants) return
    let searchResults = notificationServicesAgreementParticipants

    // ignore hyphens and case
    const sanitizedValue = sanitizeForSearch(search)
    if (sanitizedValue !== '') {
      searchResults = searchResults.filter(({ participantName }) =>
        sanitizeForSearch(participantName).includes(sanitizedValue)
      )
    }

    if (hasFiltersApplied) {
      return searchResults.filter(({ vendorDeliveryStatus }) =>
        filters.status.includes(vendorDeliveryStatus)
      )
    }

    return searchResults
  }, [
    search,
    notificationServicesAgreementParticipants,
    filters,
    hasFiltersApplied,
  ])

  if (isFetchingNotificationServicesAgreementParticipants)
    return (
      <div className="services-agreement-participants">
        <ParticipantsHeader />
        <Spinner />
      </div>
    )

  if (errorExists)
    return (
      <div className="services-agreement-participants">
        <ParticipantsHeader />
        <ComponentFailure />
      </div>
    )

  //const disabled = !filteredParticipants?.length
  //const showIcon = disabled

  return (
    <div className="services-agreement-participants">
      <div className="quick-filters-container">
        <div className="container">
          <div className="flex-columns">
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(
                  NotificationStatuses.IN_PROGRESS
                )}
                count={counts[NotificationStatuses.IN_PROGRESS] ?? 0}
                label={NotificationStatuses.IN_PROGRESS}
                icon={ClockIcon}
                onClick={() => toggleFilter(NotificationStatuses.IN_PROGRESS)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(NotificationStatuses.SENT)}
                count={counts[NotificationStatuses.SENT] ?? 0}
                label={NotificationStatuses.SENT}
                icon={EvelopeIcon}
                onClick={() => toggleFilter(NotificationStatuses.SENT)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(
                  NotificationStatuses.DELIVERED
                )}
                count={counts[NotificationStatuses.DELIVERED] ?? 0}
                label={NotificationStatuses.DELIVERED}
                icon={SuccessIcon}
                onClick={() => toggleFilter(NotificationStatuses.DELIVERED)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(NotificationStatuses.RETURN)}
                count={counts[NotificationStatuses.RETURN] ?? 0}
                label={NotificationStatuses.RETURN}
                icon={AlertIcon}
                onClick={() => toggleFilter(NotificationStatuses.RETURN)}
              />
            </div>
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={filters.status.includes(
                  NotificationStatuses.NOT_DELIVERED
                )}
                count={counts[NotificationStatuses.NOT_DELIVERED] ?? 0}
                label={NotificationStatuses.NOT_DELIVERED}
                icon={XcircleIcon}
                onClick={() => toggleFilter(NotificationStatuses.NOT_DELIVERED)}
              />
            </div>
          </div>
        </div>
      </div>
      <TableHeading title="Participants">
        {/* {!noServicesAgreementParticipants && (
          <DownloadTableButton
            data={filteredParticipants}
            fieldMappings={fieldMappings}
            fileName="Inspira Financial_Retirement Services_ARO Participants"
            disabled={disabled}
            showIcon={showIcon}
          />
        )} */}
        <div className="input-container">
          <SearchInput
            type="search"
            aria-label="Search participants"
            placeholder="Search participant name"
            onChange={(e) => handleSearch(e.target.value)}
            value={search}
          />
          <Filter
            toggleMenu={() => setShowFilterModal((s) => !s)}
            isActive={hasFiltersApplied}
          />
          {showFilterModal && (
            <ParticipantsFilterDialog
              filters={filters}
              agreementType={ProductAgreementType.NOTIFICATIONS}
              handleSubmit={(filterValues) => handleFilter(filterValues)}
              clearAllFilters={() => handleFilter(baseFilters)}
              handleClose={() => setShowFilterModal(false)}
            />
          )}
        </div>
      </TableHeading>
      <PaginatedAndSortableTable
        tableId={TABLE_ID}
        data={filteredParticipants}
        initialAscending={false}
        initialColumn="lastModifiedDate"
        parentScrollContainerSelector="#main-content"
        className="services-agreements-table"
        noRowsFoundMessage={
          <NoTableResults
            title="No results found"
            details={
              noServicesAgreementParticipants ? (
                <>
                  When a participant is added, we'll send you a notification.
                  <br />
                  You could also try adjusting your filter or search.
                </>
              ) : (
                'Try adjusting your filter or search.'
              )
            }
          />
        }
      >
        <TableColumn name="participantName" label="Participant name" />
        <TableColumn
          name="responsePeriodExpiration"
          label="Response period expiration"
          headerComponent={(props) => (
            <TableHeader {...props}>
              <Tooltip
                content={`The end of the response period given to the participant in the notification letter.`}
                ariaLabel="Delivery status"
                plugins={[]}
              >
                <InfoIcon />
              </Tooltip>
            </TableHeader>
          )}
          format={formatDate}
          sortFunc={dateSorter}
        />
        <TableColumn
          name="notificationServicesOrderName"
          label="Order number"
          format={displayAttribute}
        />
        <TableColumn
          name="vendorDeliveryStatus"
          label="Delivery status"
          headerComponent={(props) => (
            <TableHeader {...props}>
              <Tooltip
                content="To learn more about delivery statuses click on the question mark."
                ariaLabel="Delivery status"
                plugins={[]}
              >
                <QuestionIcon
                  onClick={() => setShowDeliveryStatusModal(true)}
                />
              </Tooltip>
            </TableHeader>
          )}
          format={displayAttribute}
          component={({ data, value }) => {
            const { vendorDeliveryStatus } = data
            const tooltipContent =
              ServicesAgreementParticipantsTooltip[vendorDeliveryStatus]
            return (
              <td className="status">
                {vendorDeliveryStatus ? (
                  <Tooltip
                    offset={[0, 18]}
                    content={tooltipContent}
                    ariaLabel={tooltipContent}
                  >
                    <StatusBadge status={vendorDeliveryStatus} />
                  </Tooltip>
                ) : (
                  <span>{value}</span>
                )}
              </td>
            )
          }}
        />
        <TableColumn
          name="lastModifiedDate"
          label={'Last updated'}
          format={formatDate}
          sortFunc={dateSorter}
        />
      </PaginatedAndSortableTable>
      {showDeliveryStatusModal && (
        <NotificationStatusModal
          onClose={() => setShowDeliveryStatusModal(false)}
        />
      )}
    </div>
  )
}

NotificationsAgreementParticipants.propTypes = propTypes
NotificationsAgreementParticipants.defaultProps = defaultProps

function mapStateToProps(state, { servicesAgreementId }) {
  return {
    notificationServicesAgreementParticipants:
      selectors.notificationServicesAgreementParticipants(state, {
        servicesAgreementId,
      }),
    isFetchingNotificationServicesAgreementParticipants: apiSelectors.isLoading(
      state,
      apiActions.fetchNotificationServicesAgreementParticipants
    ),
  }
}

const mapDispatchToProps = {
  fetchNotificationServicesAgreementParticipants:
    apiActions.fetchNotificationServicesAgreementParticipants,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  NotificationsAgreementParticipants
)
